import { createFileRoute, notFound } from "@tanstack/react-router";

import { PlaybookTemplate } from "~/components/cih/playbook-template";
import {
  getPublishedPlaybookWithRelationsFn,
  type PlaybookWithRelations,
} from "~/lib/functions/cih/playbooks";
import { getPublishedSeriesMembersFn } from "~/lib/functions/cih/series";
import type { GuideRow } from "~/lib/server/cih/guides";
import type { PlaybookRow } from "~/lib/server/cih/playbooks";
import {
  annotateBodyHeadings,
  type BodyHeading,
} from "~/lib/shared/cih/body-toc";
import {
  buildSeriesNav,
  type SeriesNavResult,
} from "~/lib/shared/cih/library-series";
import { buildPlaybookJsonLd } from "~/lib/shared/cih/playbook-jsonld";
import type { JsonValue } from "~/lib/shared/json";
import { createBrandMeta } from "~/lib/shared/meta";

type PlaybookSeriesNav = SeriesNavResult<
  GuideRow & { slug: string },
  PlaybookRow & { slug: string }
>;

type LoaderData = {
  title: string;
  playbook: PlaybookWithRelations;
  /** Tiptap doc with stable `id` attrs on every heading. */
  bodyDoc: JsonValue | null;
  /** Ordered H2 anchors. */
  bodyHeadings: BodyHeading[];
  /** Previous/next series nav, or null when the playbook belongs to no series. */
  seriesNav: PlaybookSeriesNav | null;
};

export const Route = createFileRoute(
  "/_public/coaching-business/playbooks/$slug",
)({
  staticData: { title: "Playbook" },
  loader: async ({ params }): Promise<LoaderData> => {
    const playbook = await getPublishedPlaybookWithRelationsFn({
      data: { slug: params.slug },
    });
    if (!playbook) throw notFound();
    const { doc: bodyDoc, headings: bodyHeadings } = annotateBodyHeadings(
      playbook.body,
    );

    // Skip the sibling fetch entirely when the playbook has no series — the
    // common case, so it should cost nothing.
    let seriesNav: LoaderData["seriesNav"] = null;
    if (playbook.seriesFamily) {
      const siblings = await getPublishedSeriesMembersFn({
        data: { family: playbook.seriesFamily },
      });
      seriesNav = buildSeriesNav(
        playbook.seriesFamily,
        siblings.guides,
        siblings.playbooks,
        { type: "playbook", slug: params.slug },
      );
    }

    return {
      title: playbook.title,
      playbook,
      bodyDoc,
      bodyHeadings,
      seriesNav,
    };
  },
  head: ({
    loaderData,
    params,
  }: {
    loaderData?: LoaderData;
    params: { slug: string };
  }) => {
    const playbook = loaderData?.playbook;
    const url = `https://dreamcoachmatch.com/coaching-business/playbooks/${params.slug}`;
    const base = createBrandMeta({
      title: playbook
        ? `${playbook.title} | Coaching Business Playbooks`
        : "Coaching Business Playbook",
      description:
        playbook?.description ||
        "A business framework analyzed through the lens of building a coaching practice.",
      ogUrl: url,
      canonical: url,
    });
    if (!playbook || !playbook.thoughtLeader) return base;

    const graph = buildPlaybookJsonLd({
      slug: params.slug,
      title: playbook.title,
      description: playbook.description,
      thoughtLeader: {
        name: playbook.thoughtLeader.name,
        organization: playbook.thoughtLeader.organization,
        website: playbook.thoughtLeader.website,
      },
      faqs: playbook.faqs ?? [],
      publishedAt: playbook.publishedAt,
      updatedAt: playbook.updatedAt,
    });

    return {
      ...base,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(graph) },
      ],
    };
  },
  component: PlaybookDetailPage,
});

function PlaybookDetailPage() {
  const { playbook, bodyDoc, bodyHeadings, seriesNav } = Route.useLoaderData();
  return (
    <PlaybookTemplate
      playbook={playbook}
      thoughtLeader={playbook.thoughtLeader}
      bodyDoc={bodyDoc}
      headings={bodyHeadings}
      seriesNav={seriesNav}
    />
  );
}
